/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import { useAuth0 } from '@auth0/auth0-react';

export interface User {
  email?: string;
  name?: string;
  picture?: string;
  sub: string; // auth0_id
}

/**
 * Custom auth hook that wraps useAuth0 with convenient methods.
 *
 * Usage:
 * const { isAuthenticated, user, login, logout, getToken } = useAuth();
 */
export const useAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error,
  } = useAuth0();

  /**
   * Redirect to Auth0 login page
   */
  const login = () => {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
  };

  /**
   * Log out and redirect to home
   */
  const logout = async () => {
    await auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  /**
   * Get access token for API calls
   */
  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.error('Failed to get access token:', error);
      // If token fetch fails, trigger re-login
      login();
      throw error;
    }
  };

  return {
    isAuthenticated,
    isLoading,
    user: user as User | undefined,
    login,
    logout,
    getToken,
    error,
  };
};

export default useAuth;
